<template>
    <div class="z-index-6 movie-cards">
        <div v-if="showSpinner" class="absolute-fill z-index-2 text-center cards-spinner">
            <i class="far fa-spinner-third fa-spin cards-spinner-icon"></i>
        </div>

        <div v-if="!movies.length && !showSpinner" class="mt-5 text-center">
            Geen films gevonden op basis van je zoekcriteria
        </div>

        <div class="row position-relative z-index-1">
            <div v-for="(movie, index) in movies" :class="columnClasses">
                <movie-card v-if="movie.title"
                            :id-prefix="movie.id"
                            :show-header="showHeader"
                            :show-times="showTimes"
                            :show-cinema-name="showCinemaName"
                            :header-text="defineHeaderText(index, movie)"
                            :poster="movie.poster || defaultPoster"
                            :title="movie.title"
                            :oneliner="movie.oneliner"
                            :single-url="getSingleUrl(movie)"
                            :trailer="movie.trailer"
                            :useIframeTag="useIframeTag(movie.trailer)"
                            :times="movie.times"
                            :show-day-in-times="hasMinimumDayFilter"
                            :ratings="movie.ratings"
                            :duplicateCounter="getDuplicateCounter(movie)"
                            :description="movie.description">
                </movie-card>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            columnClasses: {
                type: String,
                default: 'col-md-4 col-lg-3 col-xl-2',
            },
            showHeader: {
                type: Boolean,
                default: true,
            },
            showTimes: {
                type: Boolean,
                default: true,
            },
            showCinemaName: {
                type: Boolean,
                default: true,
            },
            cinemaSlug: {
                type: String
            },
            filterGroup: {
                type: String
            },
        },
        data() {
            return {
                showSpinner: true,
                noResults: false,
                movies: [],
                defaultPoster: MrMovie.defaults.imagePaths.movieSmall,
                hasMinimumDayFilter: false,
                searchByFilters: null,
                orderByValue: '',
                queryString: '',
            };
        },
        mounted() {
            // Add a cinema filter, if an ID is given
            if (this.cinemaSlug) {
                MrMovie.pageFilter.addFilters('cinema', [this.cinemaSlug]);
                MrMovie.pageFilter.filter();
            }



            VueEvents.$on('loading_items', (movies) => {
                this.showSpinner = true;
            });

            VueEvents.$on('loaded_movies', (movies) => {
                if(typeof movies.items !== 'undefined') {
                    this.setMovies(movies.items);
                }
                this.showSpinner = false;
            });

            VueEvents.$on('loaded_filters', (filters) => {

                for(let activeFilter of MrMovie.pageFilter.getActiveFilters()) {
                    if(activeFilter.id === 'day') {
                        this.hasMinimumDayFilter = (activeFilter.values[0].match(/\+$/) !== null)
                    }
                    if(activeFilter.id === 'order_by') {
                        this.orderByValue = activeFilter.values[0];
                    }
                }

                this.updateQueryString();
            });
        },
        methods: {
            /**
             * Return a new text, if it's different from the previous
             * @param {Number} index The index of the movie card
             * @param {Object} movie The movie to compare
             * @return {String|null}
             */
            defineHeaderText(index, movie) {
                // The key to compare with, to define the header text
                let compareKey = null;

                // Get the current sorting option
                if (this.orderByValue !== '') {

                        const matches = this.orderByValue.match(/(.+)_(asc|desc)$/);
                        if (matches[1] === 'time') {
                            compareKey = 'startingTime';
                        } else if (matches[1] === 'age') {
                            compareKey = 'releaseYear';
                        }

                }

                // Don't show a header text, when there is no compare key
                if (compareKey === null) {
                    return null;
                }

                // Create the value of the current movie
                let currentValue = movie[compareKey];
                if (compareKey === 'startingTime' && this.hasMinimumDayFilter) {
                    currentValue = movie['startingDay'] + ': ' + movie[compareKey];
                }

                // There is no previous movie card
                if (index === 0) {
                    return currentValue;
                }

                // Create the value of the previous movie
                const previousMovie = this.movies[index - 1];
                let previousValue = previousMovie[compareKey];
                if (compareKey === 'startingTime' && this.hasMinimumDayFilter) {
                    previousValue = previousMovie['startingDay'] + ': ' + previousMovie[compareKey];
                }

                // Value is different from the previous, use the new one
                if (previousValue && previousValue !== currentValue) {
                    return currentValue;
                }

                // Don't show a value when it's the same as the previous
                return null;
            },

            getDuplicateCounter(movie) {
                return (typeof movie.duplicateCounter !== 'undefined' && movie.duplicateCounter ? movie.duplicateCounter : 0);
            },

            setMovies(movies) {
                this.showSpinner = false;

                if (!movies.length) {
                    this.noResults = true;
                    this.movies = [];
                    VueEvents.$emit('movie_results_amount', 0);
                    return;
                } else {
                    VueEvents.$emit('movie_results_amount', movies.length);
                }

                // Improve / expand movie data
                this.movies = movies.map(movie => {
                    // Get the list of performance times
                    if (movie.performances === null) {
                        movie.times = [];
                        movie.startingTime = null;
                    } else {
                        movie.times = movie.performances
                            .map(performance => ({
                                cinemaName: performance.cinemaModel ? performance.cinemaModel.name : null,
                                cinemaSlug: performance.cinemaModel ? performance.cinemaModel.slug : null,
                                time: performance.startTime,
                                day: performance.startDay,
                                ticketUrl: performance.ticketUrl,
                                // TODO get screen type from API
                                // screen: '2d',
                            }))
                            // Cinema name is required
                            .filter(performance => performance.cinemaName !== null);

                        // The first performance is the starting time
                        if (! movie.times.length) {
                            movie.startingTime = null;
                            movie.startingDay = null;
                        } else {
                            movie.startingTime = movie.times[0].time;
                            movie.startingDay = movie.times[0].day;
                        }
                    }

                    // Define the movie poster and trailer URLs
                    movie.poster = MrMovie.mediaHelper.makeImageUrl(movie.poster, 'card');
                    movie.trailer = MrMovie.mediaHelper.makeVideoUrl(movie.trailer, 'desktop');

                    return movie;
                });
            },

            updateQueryString()
            {
                this.queryString = window.location.search;
            },

            getSingleUrl(movie)
            {
                return movie.singleUrl + this.queryString;
                //return movie.singleUrl;
            },

            useIframeTag(trailerUrl)
            {
                if (trailerUrl === null) {
                    return false;
                }

                let regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
                let match = trailerUrl.match(regExp);

                // use iframe Tag if trailerUrl is a YouTube URL
                return (match && typeof match[2] !== 'undefined' && match[2].length === 11) ? true: false;

            }
        },
    };
</script>



// WEBPACK FOOTER //
// resources/js/components/movies/MovieCards.vue